import type { BoardStatus } from "@/features/tasks/lib/board";
import { isTaskPriority, type TaskPriority } from "@/features/tasks/lib/priority";
import { NO_PROJECT_FILTER } from "@/features/tasks/lib/projectless";
import {
  ASSIGNEE_ME,
  ASSIGNEE_NONE,
  TASK_SORTS,
  type TaskSort,
} from "@/features/tasks/lib/listParams";

/** The all-projects board sorts by what changed last unless asked otherwise. */
export const DEFAULT_ALL_SORT: TaskSort = "updated";
/** Sorts that make sense across projects (the per-project number does not). */
export const ALL_TASK_SORTS = TASK_SORTS.filter((sort) => sort !== "number");

export const DUE_PRESETS = ["overdue", "today", "week", "next7", "none", "custom"] as const;
export type DuePreset = (typeof DUE_PRESETS)[number];

/** The board's filters, as they live in the URL so a filtered view can be shared. */
export interface AllTasksFilters {
  q: string;
  /** Project keys. */
  projects: string[];
  /** Client ids. */
  clients: string[];
  /** ASSIGNEE_ME, ASSIGNEE_NONE, or user ids; any of them. */
  assignees: string[];
  priorities: TaskPriority[];
  due: DuePreset | null;
  /** Only with the custom due range. */
  dueAfter: string | null;
  dueBefore: string | null;
  /** Label ids; only offered while one project is picked. */
  labels: string[];
  blocked: boolean;
  subtasks: boolean;
  sort: TaskSort;
}

const PARAM = {
  q: "q",
  projects: "project",
  clients: "client",
  assignees: "assignee",
  priorities: "priority",
  due: "due",
  dueAfter: "due_after",
  dueBefore: "due_before",
  labels: "label",
  blocked: "blocked",
  subtasks: "subtasks",
  sort: "sort",
} as const;

/** Every parameter this page owns; anything else in the URL (such as ?task=) is left alone. */
export const ALL_TASKS_PARAMS: readonly string[] = Object.values(PARAM);

const FLAG_ON = "1";
const SEPARATOR = ",";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Weeks run Saturday to Friday, as at the agency. */
const WEEK_ENDS_ON = 5;
const NEXT_DAYS = 7;

function listOf(params: URLSearchParams, name: string): string[] {
  const values = params
    .getAll(name)
    .flatMap((value) => value.split(SEPARATOR))
    .map((value) => value.trim())
    .filter(Boolean);
  return [...new Set(values)];
}

function dateOf(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  return value !== null && ISO_DATE.test(value) ? value : null;
}

function isDuePreset(value: string | null): value is DuePreset {
  return value !== null && (DUE_PRESETS as readonly string[]).includes(value);
}

function isAllSort(value: string | null): value is TaskSort {
  return value !== null && (ALL_TASK_SORTS as readonly string[]).includes(value);
}

export function parseAllTasksParams(params: URLSearchParams): AllTasksFilters {
  const due = params.get(PARAM.due);
  const sort = params.get(PARAM.sort);
  const custom = due === "custom";
  return {
    q: params.get(PARAM.q)?.trim() ?? "",
    projects: listOf(params, PARAM.projects).map((key) =>
      key.toLowerCase() === NO_PROJECT_FILTER ? NO_PROJECT_FILTER : key.toUpperCase(),
    ),
    clients: listOf(params, PARAM.clients),
    assignees: listOf(params, PARAM.assignees),
    priorities: listOf(params, PARAM.priorities).filter(isTaskPriority),
    due: isDuePreset(due) ? due : null,
    dueAfter: custom ? dateOf(params, PARAM.dueAfter) : null,
    dueBefore: custom ? dateOf(params, PARAM.dueBefore) : null,
    labels: listOf(params, PARAM.labels),
    blocked: params.get(PARAM.blocked) === FLAG_ON,
    subtasks: params.get(PARAM.subtasks) === FLAG_ON,
    sort: isAllSort(sort) ? sort : DEFAULT_ALL_SORT,
  };
}

function setOrDelete(params: URLSearchParams, name: string, value: string | null): void {
  if (value) params.set(name, value);
  else params.delete(name);
}

export function writeAllTasksParams(
  current: URLSearchParams,
  filters: AllTasksFilters,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const custom = filters.due === "custom";
  // Labels belong to one project; they mean nothing once the project choice changes.
  // Labels belong to one project; tasks outside any project (NO_PROJECT_FILTER) have none.
  const oneProject = filters.projects.length === 1 && filters.projects[0] !== NO_PROJECT_FILTER;
  const labels = oneProject ? filters.labels : [];
  setOrDelete(next, PARAM.q, filters.q.trim());
  setOrDelete(next, PARAM.projects, filters.projects.join(SEPARATOR));
  setOrDelete(next, PARAM.clients, filters.clients.join(SEPARATOR));
  setOrDelete(next, PARAM.assignees, filters.assignees.join(SEPARATOR));
  setOrDelete(next, PARAM.priorities, filters.priorities.join(SEPARATOR));
  setOrDelete(next, PARAM.due, filters.due);
  setOrDelete(next, PARAM.dueAfter, custom ? filters.dueAfter : null);
  setOrDelete(next, PARAM.dueBefore, custom ? filters.dueBefore : null);
  setOrDelete(next, PARAM.labels, labels.join(SEPARATOR));
  setOrDelete(next, PARAM.blocked, filters.blocked ? FLAG_ON : null);
  setOrDelete(next, PARAM.subtasks, filters.subtasks ? FLAG_ON : null);
  setOrDelete(next, PARAM.sort, filters.sort === DEFAULT_ALL_SORT ? null : filters.sort);
  return next;
}

export const EMPTY_ALL_TASKS_FILTERS: AllTasksFilters = parseAllTasksParams(new URLSearchParams());

/** How many filters narrow the board (search, sort and the subtask toggle do not count). */
export function activeFilterCount(filters: AllTasksFilters): number {
  return [
    filters.projects.length > 0,
    filters.clients.length > 0,
    filters.assignees.length > 0,
    filters.priorities.length > 0,
    filters.due !== null,
    filters.labels.length > 0,
    filters.blocked,
  ].filter(Boolean).length;
}

export function hasAnyFilter(filters: AllTasksFilters): boolean {
  return activeFilterCount(filters) > 0 || filters.q !== "";
}

/** Nothing narrowing the board; the sort and the subtask toggle stay as they were. */
export function clearedAllFilters(filters: AllTasksFilters): AllTasksFilters {
  return { ...EMPTY_ALL_TASKS_FILTERS, sort: filters.sort, subtasks: filters.subtasks };
}

/* ---- Quick views ---- */

export const QUICK_VIEWS = ["mine", "unassigned", "overdue", "week", "blocked"] as const;
export type QuickView = (typeof QUICK_VIEWS)[number];

const QUICK_VIEW_FILTERS: Record<QuickView, Partial<AllTasksFilters>> = {
  mine: { assignees: [ASSIGNEE_ME] },
  unassigned: { assignees: [ASSIGNEE_NONE] },
  overdue: { due: "overdue" },
  week: { due: "week" },
  blocked: { blocked: true },
};

/** Whether the filters are exactly one quick view (search and sort aside). */
export function activeQuickView(filters: AllTasksFilters): QuickView | null {
  for (const view of QUICK_VIEWS) {
    const expected = { ...clearedAllFilters(filters), q: filters.q, ...QUICK_VIEW_FILTERS[view] };
    if (sameFilters(expected, filters)) return view;
  }
  return null;
}

/** A quick view replaces the filters (keeping the search, sort and subtask toggle). */
export function applyQuickView(filters: AllTasksFilters, view: QuickView): AllTasksFilters {
  return { ...clearedAllFilters(filters), q: filters.q, ...QUICK_VIEW_FILTERS[view] };
}

function sameFilters(left: AllTasksFilters, right: AllTasksFilters): boolean {
  const empty = new URLSearchParams();
  return (
    writeAllTasksParams(empty, left).toString() === writeAllTasksParams(empty, right).toString()
  );
}

/* ---- The request ---- */

function shiftDays(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

function endOfWeek(today: string): string {
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  return shiftDays(today, (WEEK_ENDS_ON - weekday + 7) % 7);
}

export interface DueRange {
  dueAfter: string | null;
  dueBefore: string | null;
  noDue: boolean;
  /** Overdue means work still open; done work is past its date by being done. */
  openOnly: boolean;
}

export function dueRangeOf(filters: AllTasksFilters, today: string): DueRange {
  const range: DueRange = { dueAfter: null, dueBefore: null, noDue: false, openOnly: false };
  switch (filters.due) {
    case "overdue":
      return { ...range, dueBefore: shiftDays(today, -1), openOnly: true };
    case "today":
      return { ...range, dueAfter: today, dueBefore: today };
    case "week":
      return { ...range, dueAfter: today, dueBefore: endOfWeek(today) };
    case "next7":
      return { ...range, dueAfter: today, dueBefore: shiftDays(today, NEXT_DAYS - 1) };
    case "none":
      return { ...range, noDue: true };
    case "custom":
      return { ...range, dueAfter: filters.dueAfter, dueBefore: filters.dueBefore };
    case null:
      return range;
  }
}

const OPEN_COLUMNS: readonly BoardStatus[] = ["backlog", "todo", "in_progress", "in_review"];
const BOARD_STATUSES: readonly BoardStatus[] = [...OPEN_COLUMNS, "done"];

/** The search request (GET /tasks) for the board: every visible project unless narrowed. */
export function allTasksQueryOf(filters: AllTasksFilters, today: string) {
  const due = dueRangeOf(filters, today);
  // Labels belong to one project; tasks outside any project (NO_PROJECT_FILTER) have none.
  const oneProject = filters.projects.length === 1 && filters.projects[0] !== NO_PROJECT_FILTER;
  const labels = oneProject ? filters.labels : [];
  return {
    project: filters.projects.length > 0 ? filters.projects : null,
    client: filters.clients.length > 0 ? filters.clients : null,
    assignee: filters.assignees.length > 0 ? filters.assignees : null,
    status: [...(due.openOnly ? OPEN_COLUMNS : BOARD_STATUSES)],
    priority: filters.priorities.length > 0 ? filters.priorities : null,
    due_after: due.dueAfter,
    due_before: due.dueBefore,
    no_due: due.noDue || undefined,
    q: filters.q || null,
    label: labels.length > 0 ? labels : null,
    blocked: filters.blocked || undefined,
    sort: filters.sort,
  };
}
